import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { OverrideDefinition, WorkflowOverride } from '../../types';
import NotificationModal from '../shared/NotificationModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface WorkflowSlugEditorProps {
  workflowId?: string;
  initialName?: string;
  initialSlug?: string;
  onSave?: () => void;
  onCancel?: () => void;
}

const WorkflowSlugEditor: React.FC<WorkflowSlugEditorProps> = ({ workflowId, initialName, initialSlug, onSave, onCancel }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Workflow configuration
  const [workflowName, setWorkflowName] = useState(initialName || '');
  const [workflowSlug, setWorkflowSlug] = useState(initialSlug || '');
  const [eokRuntime, setEokRuntime] = useState('');
  const [eokBucketName, setEokBucketName] = useState('immar-test');
  const [eokProvider, setEokProvider] = useState('novita');
  const [eokGenerationType, setEokGenerationType] = useState('image');
  const [eokPreClassificationType, setEokPreClassificationType] = useState('permissive');
  const [eokPostClassificationType, setEokPostClassificationType] = useState('permissive');
  const [seedMode, setSeedMode] = useState('random');
  const [downloadDir, setDownloadDir] = useState('');
  const [category, setCategory] = useState('Image Generation');
  const [tags, setTags] = useState<string[]>([]);

  // Override management
  const [availableOverrides, setAvailableOverrides] = useState<OverrideDefinition[]>([]);
  const [selectedOverrides, setSelectedOverrides] = useState<WorkflowOverride[]>([]);
  const [showOverridePicker, setShowOverridePicker] = useState(false);

  // Notification modal state
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'success' | 'error' | 'info'>('info');

  // Missing slugs state
  const [missingBaseWorkflows, setMissingBaseWorkflows] = useState<string[]>([]);
  const [loadingMissingSlugs, setLoadingMissingSlugs] = useState(false);

  // Workflow-specific hygo_keys
  const [availableHygoKeys, setAvailableHygoKeys] = useState<string[]>([]);
  const [loadingWorkflowKeys, setLoadingWorkflowKeys] = useState(false);

  useEffect(() => {
    if (user?.role !== 'super_admin' && user?.role !== 'admin') {
      window.location.href = '/';
      return;
    }

    fetchOverrideDefinitions();

    if (workflowId) {
      fetchWorkflowData();
    } else {
      // Only fetch missing slugs when creating new workflow
      fetchMissingSlugs();
    }
  }, [workflowId]);

  // Fetch hygo_keys from workflow when workflowSlug changes
  useEffect(() => {
    console.log('[WorkflowSlugEditor] workflowSlug changed:', workflowSlug, 'workflowId:', workflowId);
    if (workflowSlug && !workflowId) {
      console.log('[WorkflowSlugEditor] Triggering fetchWorkflowHygoKeys...');
      fetchWorkflowHygoKeys();
    } else if (!workflowSlug) {
      console.log('[WorkflowSlugEditor] Clearing hygo_keys (no workflow slug)');
      setAvailableHygoKeys([]);
    }
  }, [workflowSlug, workflowId]);

  const fetchMissingSlugs = async () => {
    try {
      setLoadingMissingSlugs(true);
      const token = localStorage.getItem('token');
      const response = await axios.get<{ missing_base_workflows: string[] }>(
        `${API_BASE}/workflows/missing-slugs`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMissingBaseWorkflows(response.data.missing_base_workflows);
    } catch (error: any) {
      console.error('Failed to fetch missing slug workflows:', error);
    } finally {
      setLoadingMissingSlugs(false);
    }
  };

  const handleQuickFillFromMissing = (filename: string) => {
    // Remove .json extension and use as both name and slug
    const nameWithoutExtension = filename.replace('.json', '');
    setWorkflowName(nameWithoutExtension);
    setWorkflowSlug(nameWithoutExtension);

    // Scroll to the form
    window.scrollTo({ top: 0, behavior: 'smooth' });

    setNotificationMessage(`Auto-filled from "${filename}"`);
    setNotificationType('info');
    setShowNotification(true);
  };

  const fetchWorkflowHygoKeys = async () => {
    try {
      setLoadingWorkflowKeys(true);
      const token = localStorage.getItem('token');

      // Try to fetch workflow content from GitHub
      // The filename should be workflowSlug + ".json"
      const filename = workflowSlug.endsWith('.json') ? workflowSlug : `${workflowSlug}.json`;

      console.log(`[WorkflowSlugEditor] Fetching hygo_keys for: ${filename}`);

      const response = await axios.get(`${API_BASE}/base-workflows/${encodeURIComponent(filename)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('[WorkflowSlugEditor] API response:', response.data);

      const workflowJson = response.data.content;

      // Extract hygo_keys from all nodes
      const hygoKeysSet = new Set<string>();

      if (workflowJson && typeof workflowJson === 'object') {
        // Iterate through all nodes in the workflow
        for (const nodeId in workflowJson) {
          const node = workflowJson[nodeId];
          if (node && node._meta && node._meta.hygo_keys && typeof node._meta.hygo_keys === 'object') {
            // hygo_keys is an object like { "override_key": "/path/to/value" }
            // We need the KEYS (override_key), not the values
            const keys = Object.keys(node._meta.hygo_keys);
            console.log(`[WorkflowSlugEditor] Node ${nodeId} has hygo_keys:`, keys);
            keys.forEach((key: string) => hygoKeysSet.add(key));
          }
        }
      }

      const hygoKeys = Array.from(hygoKeysSet).sort();
      setAvailableHygoKeys(hygoKeys);

      console.log(`[WorkflowSlugEditor] ✓ Found ${hygoKeys.length} hygo_keys in workflow "${filename}":`, hygoKeys);

      if (hygoKeys.length > 0) {
        setNotificationMessage(`Loaded ${hygoKeys.length} parameters from workflow`);
        setNotificationType('success');
        setShowNotification(true);
      }
    } catch (error: any) {
      console.error('[WorkflowSlugEditor] ✗ Failed to fetch workflow hygo_keys:', error);
      console.error('[WorkflowSlugEditor] Error details:', error.response?.data);
      // Don't show error notification - workflow might not exist yet or have different name
      setAvailableHygoKeys([]);
    } finally {
      setLoadingWorkflowKeys(false);
    }
  };

  const fetchOverrideDefinitions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/override-definitions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAvailableOverrides(response.data);
    } catch (error) {
      console.error('Failed to fetch override definitions:', error);
      setNotificationMessage('Failed to load override definitions');
      setNotificationType('error');
      setShowNotification(true);
    }
  };

  const fetchWorkflowData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Get workflow details
      const workflowResponse = await axios.get(`${API_BASE}/workflows/${workflowId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const workflow = workflowResponse.data;
      setWorkflowName(workflow.name);
      setWorkflowSlug(workflow.workflow_slug || '');
      setEokRuntime(workflow.eok_runtime || '');
      setEokBucketName(workflow.eok_bucket_name || 'immar-test');
      setEokProvider(workflow.eok_provider || 'novita');
      setEokGenerationType(workflow.eok_generation_type || 'image');
      setEokPreClassificationType(workflow.eok_pre_classification_type || 'permissive');
      setEokPostClassificationType(workflow.eok_post_classification_type || 'permissive');
      setSeedMode(workflow.seed_mode || 'random');
      setDownloadDir(workflow.download_dir || '');
      setCategory(workflow.category || 'Image Generation');
      setTags(workflow.tags || []);

      // Get configured overrides if it's a slug workflow
      if (workflow.workflow_type === 'slug') {
        const overridesResponse = await axios.get(`${API_BASE}/workflows/${workflowId}/overrides`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setSelectedOverrides(overridesResponse.data);
      }
    } catch (error) {
      console.error('Failed to fetch workflow data:', error);
      setNotificationMessage('Failed to load workflow data');
      setNotificationType('error');
      setShowNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const addOverride = (overrideDef: OverrideDefinition) => {
    // Check if already added
    if (selectedOverrides.some(o => o.override_definition_id === overrideDef.id)) {
      setNotificationMessage('This override is already added');
      setNotificationType('info');
      setShowNotification(true);
      return;
    }

    const newOverride: WorkflowOverride = {
      id: 0, // Will be assigned by backend
      workflow_id: parseInt(workflowId || '0'),
      override_definition_id: overrideDef.id,
      override_definition: overrideDef,
      is_required: false,
      default_value: null,
      ui_parameter_name: overrideDef.override_key.replace('override_', ''), // Default to override_key without prefix
      display_order: selectedOverrides.length,
      // Joined fields
      override_key: overrideDef.override_key,
      display_name: overrideDef.display_name,
      description: overrideDef.description,
      data_type: overrideDef.data_type,
      validation_rules: overrideDef.validation_rules,
      category: overrideDef.category
    };

    setSelectedOverrides([...selectedOverrides, newOverride]);
    setShowOverridePicker(false);
  };

  const removeOverride = (overrideDefId: number) => {
    setSelectedOverrides(selectedOverrides.filter(o => o.override_definition_id !== overrideDefId));
  };

  const updateOverride = (overrideDefId: number, field: string, value: any) => {
    setSelectedOverrides(selectedOverrides.map(o =>
      o.override_definition_id === overrideDefId
        ? { ...o, [field]: value }
        : o
    ));
  };

  const moveOverride = (index: number, direction: 'up' | 'down') => {
    const newOverrides = [...selectedOverrides];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= newOverrides.length) return;

    [newOverrides[index], newOverrides[newIndex]] = [newOverrides[newIndex], newOverrides[index]];

    // Update display_order
    newOverrides.forEach((o, i) => o.display_order = i);

    setSelectedOverrides(newOverrides);
  };

  const validateForm = (): boolean => {
    if (!workflowName.trim()) {
      setNotificationMessage('Workflow name is required');
      setNotificationType('error');
      setShowNotification(true);
      return false;
    }

    if (!workflowSlug.trim()) {
      setNotificationMessage('Workflow slug is required');
      setNotificationType('error');
      setShowNotification(true);
      return false;
    }

    if (selectedOverrides.length === 0) {
      setNotificationMessage('At least one override must be configured');
      setNotificationType('error');
      setShowNotification(true);
      return false;
    }

    return true;
  };

  const saveWorkflow = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      if (workflowId) {
        // Update existing workflow
        
        // Update workflow name (NEW - allows changing name)
        await axios.put(`${API_BASE}/workflows/${workflowId}/name`, {
          name: workflowName
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        // Update EOK configuration
        await axios.put(`${API_BASE}/workflows/${workflowId}/eok-config`, {
          workflow_type: 'slug',
          workflow_slug: workflowSlug,
          eok_runtime: eokRuntime,
          eok_bucket_name: eokBucketName,
          eok_provider: eokProvider,
          eok_generation_type: eokGenerationType,
          eok_pre_classification_type: eokPreClassificationType,
          eok_post_classification_type: eokPostClassificationType,
          seed_mode: seedMode,
          download_dir: downloadDir
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        // Update organization
        await axios.put(`${API_BASE}/workflows/${workflowId}/organization`, {
          category,
          tags
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        // Get existing overrides to determine what to add/update/delete
        const existingResponse = await axios.get(`${API_BASE}/workflows/${workflowId}/overrides`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const existingOverrides = existingResponse.data;

        // Delete removed overrides
        for (const existing of existingOverrides) {
          if (!selectedOverrides.some(o => o.override_definition_id === existing.override_definition_id)) {
            await axios.delete(`${API_BASE}/workflows/${workflowId}/overrides/${existing.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
          }
        }

        // Add or update overrides
        for (const override of selectedOverrides) {
          const existing = existingOverrides.find((e: any) =>
            e.override_definition_id === override.override_definition_id
          );

          if (existing) {
            // Update existing
            await axios.put(
              `${API_BASE}/workflows/${workflowId}/overrides/${existing.id}`,
              {
                is_required: override.is_required,
                default_value: override.default_value,
                ui_parameter_name: override.ui_parameter_name,
                display_order: override.display_order
              },
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
          } else {
            // Add new
            await axios.post(
              `${API_BASE}/workflows/${workflowId}/overrides`,
              {
                override_definition_id: override.override_definition_id,
                is_required: override.is_required,
                default_value: override.default_value,
                ui_parameter_name: override.ui_parameter_name,
                display_order: override.display_order
              },
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
          }
        }

        setNotificationMessage('Workflow updated successfully!');
        setNotificationType('success');
        setShowNotification(true);
      } else {
        // CREATE NEW WORKFLOW - UPDATED TO USE NEW ENDPOINT
        const createResponse = await axios.post(
          `${API_BASE}/workflows`,
          {
            name: workflowName,
            workflow_type: 'slug',
            workflow_slug: workflowSlug,
            category,
            tags,
            eok_runtime: eokRuntime,
            eok_bucket_name: eokBucketName,
            eok_provider: eokProvider,
            eok_generation_type: eokGenerationType,
            eok_pre_classification_type: eokPreClassificationType,
            eok_post_classification_type: eokPostClassificationType,
            seed_mode: seedMode,
            download_dir: downloadDir
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        const newWorkflow = createResponse.data;
        const newWorkflowId = newWorkflow.id;

        // Add all configured overrides to the new workflow
        for (const override of selectedOverrides) {
          await axios.post(
            `${API_BASE}/workflows/${newWorkflowId}/overrides`,
            {
              override_definition_id: override.override_definition_id,
              is_required: override.is_required,
              default_value: override.default_value,
              ui_parameter_name: override.ui_parameter_name,
              display_order: override.display_order
            },
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
        }

        setNotificationMessage('Workflow created successfully!');
        setNotificationType('success');
        setShowNotification(true);

        // Redirect to the new workflow editor
        setTimeout(() => {
          window.location.href = `/admin/workflow/${newWorkflowId}/slug`;
        }, 1500);
      }

      if (onSave) onSave();
    } catch (error: any) {
      console.error('Failed to save workflow:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save workflow';
      setNotificationMessage(errorMessage);
      setNotificationType('error');
      setShowNotification(true);
    } finally {
      setSaving(false);
    }
  };

  const categories = [
    'Image Generation',
    'Text Processing',
    'Video Generation',
    'Audio Processing',
    'General'
  ];

  const providers = ['novita', 'vast', 'novita-serverless'];
  const classificationTypes = ['permissive', 'strict'];

  if (!user || (user.role !== 'super_admin' && user.role !== 'admin')) {
    return <div className="text-red-600">Access denied. Admin privileges required.</div>;
  }

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900">
          {workflowId ? 'Edit Slug Workflow' : 'Create Slug Workflow'}
        </h2>
        <div className="flex gap-3">
          {onCancel && (
            <button
              onClick={onCancel}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            onClick={saveWorkflow}
            disabled={saving}
            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold py-2 px-6 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Workflow'}
          </button>
        </div>
      </div>

      {/* Missing Slugs Banner - Only show when creating new workflow */}
      {!workflowId && missingBaseWorkflows.length > 0 && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <span>💡</span>
            Quick Create from Base Workflows ({missingBaseWorkflows.length})
          </h3>
          <p className="text-sm text-blue-800 mb-3">
            These base workflows don't have slug versions yet. Click any to auto-fill the form.
          </p>
          <div className="flex flex-wrap gap-2">
            {missingBaseWorkflows.map((filename) => (
              <button
                key={filename}
                onClick={() => handleQuickFillFromMissing(filename)}
                className="px-3 py-1.5 bg-white border border-blue-300 rounded-md text-sm font-mono text-blue-900 hover:bg-blue-100 hover:border-blue-400 transition-colors"
              >
                {filename}
              </button>
            ))}
          </div>
        </div>
      )}

      {loadingMissingSlugs && !workflowId && missingBaseWorkflows.length === 0 && (
        <div className="mb-6 text-sm text-gray-500 italic">
          Analyzing base workflows for missing slug versions...
        </div>
      )}

      <div className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Workflow Name *
              </label>
              <input
                type="text"
                value={workflowName}
                onChange={(e) => setWorkflowName(e.target.value)}
                placeholder="e.g., Portrait Generator"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* EOK Configuration */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">EOK Configuration</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Workflow Slug *
              </label>
              <input
                type="text"
                value={workflowSlug}
                onChange={(e) => setWorkflowSlug(e.target.value)}
                placeholder="e.g., L1_YY_10_3_2025"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">The EOK server-side workflow identifier</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Runtime
              </label>
              <input
                type="text"
                value={eokRuntime}
                onChange={(e) => setEokRuntime(e.target.value)}
                placeholder="e.g., hygoinc/mainlive:5v09292025"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Docker image or endpoint name</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Provider
              </label>
              <select
                value={eokProvider}
                onChange={(e) => setEokProvider(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {providers.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Generation Type
              </label>
              <select
                value={eokGenerationType}
                onChange={(e) => setEokGenerationType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="image">Image</option>
                <option value="video">Video</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Determines which EOK endpoint to use</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bucket Name
              </label>
              <input
                type="text"
                value={eokBucketName}
                onChange={(e) => setEokBucketName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pre-Classification Type
              </label>
              <select
                value={eokPreClassificationType}
                onChange={(e) => setEokPreClassificationType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {classificationTypes.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Post-Classification Type
              </label>
              <select
                value={eokPostClassificationType}
                onChange={(e) => setEokPostClassificationType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {classificationTypes.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seed Mode
              </label>
              <select
                value={seedMode}
                onChange={(e) => setSeedMode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="random">Random</option>
                <option value="fixed">Fixed</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Random generates different seeds per image</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Download Directory
              </label>
              <input
                type="text"
                value={downloadDir}
                onChange={(e) => setDownloadDir(e.target.value)}
                placeholder="e.g., 10_3_2025/SZ_PS_Test"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Output directory path on EOK server</p>
            </div>
          </div>
        </div>

        {/* Override Configuration */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-gray-900">Parameter Overrides</h3>
            <button
              onClick={() => setShowOverridePicker(true)}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors text-sm"
            >
              + Add Override
            </button>
          </div>

          {selectedOverrides.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No overrides configured. Click "Add Override" to get started.
            </div>
          ) : (
            <div className="space-y-3">
              {selectedOverrides.map((override, index) => (
                <div key={override.override_definition_id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">{override.display_name}</span>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {override.data_type}
                        </span>
                        <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                          {override.category}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{override.description}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Override key: <code className="bg-gray-200 px-1 rounded">{override.override_key}</code>
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => moveOverride(index, 'up')}
                        disabled={index === 0}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => moveOverride(index, 'down')}
                        disabled={index === selectedOverrides.length - 1}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                        title="Move down"
                      >
                        ↓
                      </button>
                      <button
                        onClick={() => removeOverride(override.override_definition_id)}
                        className="text-red-500 hover:text-red-700 ml-2"
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Default Value
                      </label>
                      <input
                        type="text"
                        value={override.default_value || ''}
                        onChange={(e) => updateOverride(override.override_definition_id, 'default_value', e.target.value)}
                        placeholder="Optional"
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    <div className="flex items-center">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={override.is_required}
                          onChange={(e) => updateOverride(override.override_definition_id, 'is_required', e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Required</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Override Picker Modal */}
      {showOverridePicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowOverridePicker(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Add Override</h3>
                  {availableHygoKeys.length > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      Showing {availableOverrides.filter(o => availableHygoKeys.includes(o.override_key)).length} overrides available in this workflow
                    </p>
                  )}
                  {loadingWorkflowKeys && (
                    <p className="text-sm text-blue-600 mt-1">
                      Loading workflow parameters...
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setShowOverridePicker(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                {(() => {
                  console.log('[WorkflowSlugEditor] Modal opened. Available hygo_keys:', availableHygoKeys);
                  console.log('[WorkflowSlugEditor] Total override definitions:', availableOverrides.length);

                  const filteredOverrides = availableOverrides.filter(override => {
                    // If we have hygo_keys from the workflow, only show matching overrides
                    if (availableHygoKeys.length > 0) {
                      const matches = availableHygoKeys.includes(override.override_key);
                      if (!matches) {
                        console.log(`[WorkflowSlugEditor] Filtering out: ${override.override_key}`);
                      }
                      return matches;
                    }
                    // Otherwise show all overrides
                    return true;
                  });

                  console.log('[WorkflowSlugEditor] Filtered overrides:', filteredOverrides.length);

                  if (filteredOverrides.length === 0 && availableHygoKeys.length > 0) {
                    return (
                      <div className="text-center py-8 text-gray-500">
                        <p className="mb-2">No matching overrides found for this workflow.</p>
                        <p className="text-sm">Make sure the workflow slug matches an existing base workflow file.</p>
                      </div>
                    );
                  }

                  return Object.entries(
                    filteredOverrides.reduce((acc, override) => {
                      if (!acc[override.category]) acc[override.category] = [];
                      acc[override.category].push(override);
                      return acc;
                    }, {} as Record<string, OverrideDefinition[]>)
                  ).map(([category, overrides]) => (
                  <div key={category}>
                    <h4 className="font-semibold text-gray-700 mb-2">{category}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {overrides.map(override => {
                        const isAdded = selectedOverrides.some(o => o.override_definition_id === override.id);
                        return (
                          <button
                            key={override.id}
                            onClick={() => addOverride(override)}
                            disabled={isAdded}
                            className={`text-left p-3 rounded-lg border transition-colors ${
                              isAdded
                                ? 'bg-gray-100 border-gray-300 cursor-not-allowed opacity-50'
                                : 'bg-white border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{override.display_name}</div>
                                <div className="text-xs text-gray-500 mt-1">{override.description}</div>
                                <div className="text-xs text-gray-400 mt-1">
                                  {override.override_key} • {override.data_type}
                                </div>
                              </div>
                              {isAdded && (
                                <span className="text-green-600 text-sm ml-2">✓ Added</span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={showNotification}
        onClose={() => setShowNotification(false)}
        message={notificationMessage}
        type={notificationType}
      />
    </div>
  );
};

export default WorkflowSlugEditor;
